import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Search,
  Calendar,
  ChevronDown,
  Banknote,
  Clock,
  CheckCircle,
  XCircle,
  Download,
  RefreshCcw,
  Eye,
  Send,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface PayoutRecord {
  id: string
  affiliateCode: string
  affiliateName: string
  bankName: string
  accountNumber: string
  accountName: string
  amount: number
  fee: number
  netAmount: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  requestDate: string
  processDate?: string
  note?: string
}

const mockPayouts: PayoutRecord[] = [
  {
    id: 'PAY001',
    affiliateCode: 'AFF001',
    affiliateName: 'สมชาย ใจดี',
    bankName: 'กสิกรไทย',
    accountNumber: '123-4-56789-0',
    accountName: 'นาย สมชาย ใจดี',
    amount: 125000,
    fee: 30,
    netAmount: 124970,
    status: 'pending',
    requestDate: '2025-11-27 10:30',
  },
  {
    id: 'PAY002',
    affiliateCode: 'AFF002',
    affiliateName: 'วิชัย มั่งมี',
    bankName: 'ไทยพาณิชย์',
    accountNumber: '234-5-67890-1',
    accountName: 'นาย วิชัย มั่งมี',
    amount: 85000,
    fee: 30,
    netAmount: 84970,
    status: 'processing',
    requestDate: '2025-11-27 09:15',
  },
  {
    id: 'PAY003',
    affiliateCode: 'AFF003',
    affiliateName: 'พิมพ์ใจ สวยงาม',
    bankName: 'กรุงเทพ',
    accountNumber: '345-6-78901-2',
    accountName: 'นางสาว พิมพ์ใจ สวยงาม',
    amount: 42000,
    fee: 30,
    netAmount: 41970,
    status: 'completed',
    requestDate: '2025-11-26 14:20',
    processDate: '2025-11-26 15:45',
  },
  {
    id: 'PAY004',
    affiliateCode: 'AFF001',
    affiliateName: 'สมชาย ใจดี',
    bankName: 'กสิกรไทย',
    accountNumber: '123-4-56789-0',
    accountName: 'นาย สมชาย ใจดี',
    amount: 142000,
    fee: 30,
    netAmount: 141970,
    status: 'completed',
    requestDate: '2025-11-25 11:00',
    processDate: '2025-11-25 12:30',
  },
  {
    id: 'PAY005',
    affiliateCode: 'AFF004',
    affiliateName: 'อนุชา รวยเร็ว',
    bankName: 'กรุงไทย',
    accountNumber: '456-7-89012-3',
    accountName: 'นาย อนุชา รวยเร็ว',
    amount: 28000,
    fee: 30,
    netAmount: 27970,
    status: 'failed',
    requestDate: '2025-11-24 16:45',
    note: 'เลขบัญชีไม่ถูกต้อง',
  },
]

const bankColors: Record<string, string> = {
  'กสิกรไทย': 'bg-green-600',
  'ไทยพาณิชย์': 'bg-purple-600',
  'กรุงเทพ': 'bg-blue-700',
  'กรุงไทย': 'bg-cyan-600',
}

export function PayoutsPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedPayout, setSelectedPayout] = useState<PayoutRecord | null>(null)

  const filteredPayouts = useMemo(() => {
    let result = mockPayouts
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(p =>
        p.affiliateName.toLowerCase().includes(term) ||
        p.id.toLowerCase().includes(term) ||
        p.accountNumber.includes(term)
      )
    }
    if (statusFilter !== 'all') {
      result = result.filter(p => p.status === statusFilter)
    }
    return result
  }, [searchTerm, statusFilter])

  const stats = useMemo(() => ({
    pending: mockPayouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0),
    processing: mockPayouts.filter(p => p.status === 'processing').reduce((sum, p) => sum + p.amount, 0),
    completed: mockPayouts.filter(p => p.status === 'completed').reduce((sum, p) => sum + p.amount, 0),
    totalPaid: mockPayouts.filter(p => p.status === 'completed').length,
  }), [])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const getStatusBadge = (status: PayoutRecord['status']) => {
    const config = {
      pending: { label: 'รอดำเนินการ', icon: <Clock className="h-3 w-3" />, className: 'bg-warning/10 text-warning' },
      processing: { label: 'กำลังโอน', icon: <Send className="h-3 w-3" />, className: 'bg-blue-500/10 text-blue-500' },
      completed: { label: 'สำเร็จ', icon: <CheckCircle className="h-3 w-3" />, className: 'bg-win/10 text-win' },
      failed: { label: 'ล้มเหลว', icon: <XCircle className="h-3 w-3" />, className: 'bg-destructive/10 text-destructive' },
    }
    const { label, icon, className } = config[status]
    return (
      <Badge variant="secondary" className={cn("border-0 gap-1", className)}>
        {icon} {label}
      </Badge>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">การจ่ายเงิน</h1>
          <p className="text-muted-foreground">จัดการคำขอถอนค่าคอมมิชชั่นของพันธมิตร</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon">
            <RefreshCcw className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            ส่งออก
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-warning/10">
                <Clock className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">รอดำเนินการ</p>
                <p className="text-xl font-bold text-warning">{formatCurrency(stats.pending)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <Send className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">กำลังโอน</p>
                <p className="text-xl font-bold text-blue-500">{formatCurrency(stats.processing)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-win/10">
                <CheckCircle className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">จ่ายแล้ว</p>
                <p className="text-xl font-bold text-win">{formatCurrency(stats.completed)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <Banknote className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">รายการสำเร็จ</p>
                <p className="text-xl font-bold">{stats.totalPaid} รายการ</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payouts Table */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหา รหัส, ชื่อ, เลขบัญชี..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Calendar className="h-4 w-4" />
                  {statusFilter === 'all' ? 'ทุกสถานะ' : getStatusBadge(statusFilter as PayoutRecord['status'])}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setStatusFilter('all')}>ทุกสถานะ</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('pending')}>รอดำเนินการ</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('processing')}>กำลังโอน</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('completed')}>สำเร็จ</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('failed')}>ล้มเหลว</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-y">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">รหัส</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">พันธมิตร</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">บัญชีธนาคาร</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">จำนวน</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">สถานะ</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">วันที่ขอ</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayouts.map((payout) => (
                  <tr key={payout.id} className="border-b hover:bg-muted/30">
                    <td className="px-4 py-4">
                      <Badge variant="outline" className="font-mono">{payout.id}</Badge>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium">{payout.affiliateName}</p>
                        <p className="text-xs text-muted-foreground">{payout.affiliateCode}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className={cn("w-2 h-8 rounded-full", bankColors[payout.bankName] || 'bg-gray-400')} />
                        <div>
                          <p className="font-medium">{payout.bankName}</p>
                          <p className="text-xs text-muted-foreground">{payout.accountNumber}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div>
                        <p className="font-semibold">{formatCurrency(payout.amount)}</p>
                        <p className="text-xs text-muted-foreground">ค่าธรรมเนียม: ฿{payout.fee}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {getStatusBadge(payout.status)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <p className="text-sm">{payout.requestDate}</p>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => setSelectedPayout(payout)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {payout.status === 'pending' && (
                          <Button size="sm" className="bg-win hover:bg-win/90">อนุมัติ</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Payout Detail Dialog */}
      <Dialog open={!!selectedPayout} onOpenChange={() => setSelectedPayout(null)}>
        <DialogContent className="max-w-md">
          {selectedPayout && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Banknote className="h-5 w-5" />
                  รายละเอียดการจ่ายเงิน
                </DialogTitle>
                <DialogDescription>{selectedPayout.id}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">สถานะ</span>
                  {getStatusBadge(selectedPayout.status)}
                </div>

                <div className="p-4 rounded-lg bg-muted/30 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className={cn("w-3 h-10 rounded-full", bankColors[selectedPayout.bankName] || 'bg-gray-400')} />
                    <div>
                      <p className="font-medium">{selectedPayout.bankName}</p>
                      <p className="text-sm text-muted-foreground">{selectedPayout.accountNumber}</p>
                      <p className="text-sm">{selectedPayout.accountName}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">จำนวนเงิน</span>
                    <span className="font-medium">{formatCurrency(selectedPayout.amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ค่าธรรมเนียม</span>
                    <span className="text-destructive">-{formatCurrency(selectedPayout.fee)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-medium">ยอดสุทธิ</span>
                    <span className="text-xl font-bold text-win">{formatCurrency(selectedPayout.netAmount)}</span>
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">วันที่ขอ</span>
                    <span>{selectedPayout.requestDate}</span>
                  </div>
                  {selectedPayout.processDate && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">วันที่โอน</span>
                      <span>{selectedPayout.processDate}</span>
                    </div>
                  )}
                  {selectedPayout.note && (
                    <div className="p-2 rounded bg-destructive/10 text-destructive text-xs">
                      {selectedPayout.note}
                    </div>
                  )}
                </div>

                {selectedPayout.status === 'pending' && (
                  <div className="flex gap-2 pt-4">
                    <Button variant="destructive" className="flex-1">ปฏิเสธ</Button>
                    <Button className="flex-1 bg-win hover:bg-win/90">อนุมัติ</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}










